<template>
    <Tooltip :is-disabled="!isDisabled" :is-teleported="true">
        <template #trigger="disabledTooltipProps">
            <div
                class="w-20.75 h-5 relative rounded-full p-0.5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-2 focus-visible:z-10 ring-1 ring-gray-50"
                :class="[isError ? 'bg-red-200' : 'bg-gray-200', { 'cursor-not-allowed': isDisabled }]"
                :style="colorStyle"
                role="radiogroup"
                :aria-disabled="isDisabled ? true : undefined"
                :tabindex="0"
                v-bind="disabledTooltipProps"
                @keydown="handleKeyDown"
            >
                <Tooltip
                    class="absolute left-0.5"
                    :class="{ 'pointer-events-none': isDisabled && !isLeftPosition }"
                    :is-disabled="isDisabled"
                    :is-teleported="true"
                >
                    <template #trigger="tooltipPropsLeft">
                        <button
                            class="h-4 rounded-full flex flex-row items-center justify-center overflow-hidden text-gray-900"
                            :class="[
                                getCursorClass(isLeftPosition, isLoading, isError),
                                isLeftPosition
                                    ? 'w-8.75 z-0 shadow-[0_0_1px_rgba(0,0,0,0.5)]'
                                    : 'w-6.5 hover:w-8.75 transition-[width] duration-100 group z-10 opacity-0 hover:opacity-100 hover:shadow-[0_0_1px_rgba(0,0,0,0.5)]',
                                {
                                    'bg-red-700 text-red-50 hover:bg-red-900 active:bg-red-950':
                                        props.status === 'Strong-Error',
                                },
                            ]"
                            role="radio"
                            :aria-checked="isLeftPosition"
                            :aria-current="isLeftPosition"
                            :tabindex="-1"
                            v-bind="tooltipPropsLeft"
                            @click="handleClick('Strong')"
                        >
                            <AllocationPart
                                v-if="props.status !== 'Strong-Error'"
                                :color="segmentColor"
                                form="Single"
                                :is-phase-end="false"
                                variant="Strong-Mandatory"
                                :specialization="null"
                                :has-temporary-change-marker="false"
                            />
                            <VisibilityIcon
                                v-if="props.status !== 'Strong-Error' && props.status !== 'Strong-Loading'"
                                class="size-3.5 absolute overflow-visible text-(--color-icon-text)"
                                :class="{
                                    'opacity-0 transition-opacity duration-100 group-hover:opacity-100':
                                        !isLeftPosition,
                                }"
                            />
                            <PendingIcon
                                v-if="props.status === 'Strong-Loading'"
                                class="animate-spin size-3 absolute text-(--color-icon-text)"
                            />
                            <RefreshIcon
                                v-if="props.status === 'Strong-Error'"
                                class="size-3.5 absolute text-(--color-icon-text)"
                            />

                            <span class="sr-only">
                                {{ $t('accessibility-label-prio-1-visible') }}
                            </span>
                        </button>
                    </template>

                    <template #header>
                        <p>{{ $t('external-events-default-visibility-in-timeline') }}</p>
                    </template>

                    <template #text>
                        <p>{{ $t('default-prio-1-visible') }}</p>
                    </template>
                </Tooltip>

                <Tooltip
                    class="absolute left-1/2 -translate-x-1/2"
                    :class="{ 'pointer-events-none': isDisabled && !isMiddlePosition }"
                    :is-disabled="isDisabled"
                    :is-teleported="true"
                >
                    <template #trigger="tooltipPropsMiddle">
                        <button
                            class="h-4 rounded-full flex flex-row items-center justify-center overflow-hidden text-gray-900"
                            :class="[
                                getCursorClass(isMiddlePosition, isLoading, isError),
                                isMiddlePosition
                                    ? 'w-8.75 z-0 shadow-[0_0_1px_rgba(0,0,0,0.5)]'
                                    : 'w-6.5 hover:w-8.75 transition-[width] duration-100 group z-10 opacity-0 hover:opacity-100 hover:shadow-[0_0_1px_rgba(0,0,0,0.5)]',
                                {
                                    'bg-red-700 text-red-50 hover:bg-red-900 active:bg-red-950':
                                        props.status === 'Inner-Strong-Error',
                                },
                            ]"
                            :aria-checked="isMiddlePosition"
                            :aria-current="isMiddlePosition"
                            :tabindex="-1"
                            v-bind="tooltipPropsMiddle"
                            @click="handleClick('Inner-Strong')"
                        >
                            <AllocationPart
                                v-if="props.status !== 'Inner-Strong-Error'"
                                :color="segmentColor"
                                form="Single"
                                :is-phase-end="false"
                                variant="Inner-Strong-Mandatory"
                                :specialization="null"
                                :has-temporary-change-marker="false"
                            />
                            <VisibilityIcon
                                v-if="props.status !== 'Inner-Strong-Loading' && props.status !== 'Inner-Strong-Error'"
                                class="size-3.5 absolute overflow-visible text-(--color-icon-text)"
                                :class="{
                                    'opacity-0 transition-opacity duration-100 group-hover:opacity-100':
                                        !isMiddlePosition,
                                }"
                            />
                            <PendingIcon
                                v-if="props.status === 'Inner-Strong-Loading'"
                                class="animate-spin size-3 absolute text-(--color-icon-text)"
                            />
                            <RefreshIcon
                                v-if="props.status === 'Inner-Strong-Error'"
                                class="size-3.5 absolute text-(--color-icon-text)"
                            />

                            <span class="sr-only">
                                {{ $t('accessibility-label-prio-2-visible') }}
                            </span>
                        </button>
                    </template>

                    <template #header>
                        <p>{{ $t('external-events-default-visibility-in-timeline') }}</p>
                    </template>

                    <template #text>
                        <p>{{ $t('default-prio-2-visible') }}</p>
                    </template>
                </Tooltip>

                <Tooltip
                    class="absolute right-0.5"
                    :class="{ 'pointer-events-none': isDisabled && !isRightPosition }"
                    :is-disabled="isDisabled"
                    :is-teleported="true"
                >
                    <template #trigger="tooltipPropsRight">
                        <button
                            class="h-4 w-6.5 rounded-full flex flex-row items-center justify-center overflow-hidden bg-gray-50 text-gray-900"
                            :class="[
                                getCursorClass(isRightPosition, isLoading, isError),
                                isRightPosition
                                    ? 'w-8.75 z-0 shadow-[0_0_1px_rgba(0,0,0,0.5)]'
                                    : 'w-6.5 hover:w-8.75 transition-[width] duration-100 group z-10 opacity-0 hover:opacity-100 hover:shadow-[0_0_1px_rgba(0,0,0,0.5)]',
                                {
                                    'bg-red-700 text-red-50 hover:bg-red-900 active:bg-red-950':
                                        props.status === 'Invisible-Error',
                                },
                            ]"
                            :aria-checked="isRightPosition"
                            :aria-current="isRightPosition"
                            :tabindex="-1"
                            v-bind="tooltipPropsRight"
                            @click="handleClick('Invisible')"
                        >
                            <VisibilityOffIcon
                                v-if="props.status !== 'Invisible-Loading' && props.status !== 'Invisible-Error'"
                                class="size-3.5 absolute"
                                :class="{
                                    'opacity-0 transition-opacity duration-100 group-hover:opacity-100':
                                        !isRightPosition,
                                    'text-gray-500': isDisabled,
                                }"
                            />
                            <PendingIcon
                                v-if="props.status === 'Invisible-Loading'"
                                class="animate-spin size-3 absolute"
                            />
                            <RefreshIcon
                                v-if="props.status === 'Invisible-Error'"
                                class="size-3.5 absolute text-(--color-icon-text)"
                            />

                            <span class="sr-only">
                                {{ $t('accessibility-label-invisible') }}
                            </span>
                        </button>
                    </template>

                    <template #header>
                        <p>{{ $t('external-events-default-visibility-in-timeline') }}</p>
                    </template>

                    <template #text>
                        <p>{{ $t('default-not-visible') }}</p>
                    </template>
                </Tooltip>
            </div>
        </template>

        <template #header>
            <p>{{ $t('external-events-default-visibility-in-timeline') }}</p>
        </template>

        <template #text>
            <p>{{ props.disabledExplanation }}</p>
            <div class="mt-1 border-t border-gray-200 pt-1 text-gray-600">
                <p v-if="isLeftPosition">{{ $t('default-prio-1-visible') }}</p>
                <p v-else-if="isMiddlePosition">{{ $t('default-prio-2-visible') }}</p>
                <p v-else>{{ $t('default-not-visible') }}</p>
            </div>
        </template>
    </Tooltip>
</template>

<script setup lang="ts">
import { computed, provide, ref } from 'vue';
import PendingIcon from '../Icon/PendingIcon.vue';
import RefreshIcon from '../Icon/RefreshIcon.vue';
import VisibilityIcon from '../Icon/VisibilityIcon.vue';
import VisibilityOffIcon from '../Icon/VisibilityOffIcon.vue';
import { PhaseColor } from '../Timeline/Timeline.types';
import AllocationPart from '../Timeline/allocation/AllocationPart.vue';
import { getTextColorForPhaseType } from '../Timeline/phaseColors';
import Tooltip from '../Tooltip/Tooltip.vue';
import { triggerHideTooltipKey } from '../Tooltip/injectionKeys';

export interface ExternalEventsTriStateProps {
    status:
        | 'Strong'
        | 'Strong-Loading'
        | 'Strong-Error'
        | 'Inner-Strong'
        | 'Inner-Strong-Loading'
        | 'Inner-Strong-Error'
        | 'Invisible'
        | 'Invisible-Loading'
        | 'Invisible-Error';
    color: Extract<
        PhaseColor,
        'External-Event-Category-Project' | 'External-Event-Category-Event' | 'External-Event-Category-External-Event'
    >;
    disabledExplanation?: string;
}

type ExternalEventsTriStateEmits = {
    (e: 'changeStatus', newStatus: ExternalEventsTriStateProps['status']): void;
};

const props = defineProps<ExternalEventsTriStateProps>();
const emit = defineEmits<ExternalEventsTriStateEmits>();

const isDisabled = computed(() => Boolean(props.disabledExplanation));

const segmentColor = computed<PhaseColor>(() => (isDisabled.value ? 'Aggregation' : props.color));

const isError = computed(() => {
    return (
        props.status === 'Strong-Error' || props.status === 'Inner-Strong-Error' || props.status === 'Invisible-Error'
    );
});

const isLoading = computed(() => {
    return (
        props.status === 'Strong-Loading' ||
        props.status === 'Inner-Strong-Loading' ||
        props.status === 'Invisible-Loading'
    );
});

const isLeftPosition = computed(() => {
    return props.status === 'Strong' || props.status === 'Strong-Loading' || props.status === 'Strong-Error';
});

const isMiddlePosition = computed(() => {
    return (
        props.status === 'Inner-Strong' ||
        props.status === 'Inner-Strong-Loading' ||
        props.status === 'Inner-Strong-Error'
    );
});

const isRightPosition = computed(() => {
    return props.status === 'Invisible' || props.status === 'Invisible-Loading' || props.status === 'Invisible-Error';
});

function getCursorClass(isActive: boolean, isLoading: boolean, isError: boolean) {
    if (isDisabled.value) {
        return 'cursor-not-allowed';
    }

    if (isActive && !isLoading && !isError) {
        return 'cursor-default';
    }

    if (isActive && isLoading) {
        return 'cursor-wait';
    }

    return 'cursor-pointer';
}

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}

function handleClick(status: 'Strong' | 'Inner-Strong' | 'Invisible') {
    // The tooltip stays open, as it explains why nothing happens
    if (isDisabled.value) {
        return;
    }

    triggerHideTooltip();

    if (props.status !== status) {
        emit('changeStatus', status);
    }
}

function handleKeyDown(event: KeyboardEvent) {
    if (isDisabled.value) {
        return;
    }

    switch (event.key) {
        case 'ArrowLeft':
            if (isRightPosition.value) {
                emit('changeStatus', 'Inner-Strong');
            } else if (isMiddlePosition.value) {
                emit('changeStatus', 'Strong');
            }
            event.preventDefault();
            break;

        case 'ArrowRight':
            if (isLeftPosition.value) {
                emit('changeStatus', 'Inner-Strong');
            } else if (isMiddlePosition.value) {
                emit('changeStatus', 'Invisible');
            }
            event.preventDefault();
            break;

        case 'PageUp':
        case 'Home':
            if (props.status !== 'Strong') {
                emit('changeStatus', 'Strong');
            }
            event.preventDefault();
            break;

        case 'PageDown':
        case 'End':
            if (props.status !== 'Invisible') {
                emit('changeStatus', 'Invisible');
            }
            event.preventDefault();
            break;
    }
}

const colorStyle = computed(() => {
    return {
        '--color-icon-text': `var(${getTextColorForPhaseType(segmentColor.value)})`,
    };
});
</script>
